import { camelCase, upperFirst } from 'lodash-es';
import unraw from 'unraw';
export function pascalCase(str) {
    return upperFirst(camelCase(str));
}
export function escapeSequenceChar(str) {
    /**
     * Use JSON.stringify to escape the escape sequence
     * Remove double quote padding and escaping from double quotes
     * Also remove double escaping of slash
     */
    const val = JSON.stringify(str)
        .replace(/^"(.*)"$/, '$1')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
    return val;
}
export function unescapeSequenceChar(str) {
    try {
        return unraw(str, true);
    }
    catch (e) {
        // in case of string as malformed unicode just return the whole string
        return str;
    }
}
export function pluralize(num, word, suffix = 's') {
    return `${word}${Math.abs(num) === 1 ? '' : suffix}`;
}
export function pluralizeWithNumber(num, word, suffix = 's') {
    return `${new Intl.NumberFormat().format(num)} ${pluralize(num, word, suffix)}`;
}
export function pluralVerb(num, singular, plural) {
    return num === 1 ? singular : plural;
}
export function parseJSON(json, fallbackValue) {
    try {
        if (!json) {
            return fallbackValue;
        }
        return JSON.parse(json);
    }
    catch (e) {
        return fallbackValue;
    }
}
function initials(fullName, delimiter = ' ') {
    let [first, ...rest] = fullName.trim().split(delimiter);
    let last = rest[rest.length - 1];
    first = first || '';
    last = last || '';
    return `${first.charAt(0).toUpperCase()}${last.charAt(0).toUpperCase()}`;
}
export function nameInitials(fullName) {
    if (fullName.includes('.')) {
        return initials(fullName, '.');
    }
    else if (fullName.includes('_')) {
        return initials(fullName, '_');
    }
    else if (fullName.includes('-')) {
        return initials(fullName, '-');
    }
    return initials(fullName);
}
export function emailInitials(email) {
    const [userName] = email.split('@');
    return nameInitials(userName);
}
export function removeTrailingSlash(path) {
    return path.replace(/\/$/, '');
}
export function stringFormatter(str, ...args) {
    return str.replace(/{(\d+)}/g, function (match, number) {
        return typeof args[number] != 'undefined' ? args[number] : match;
    });
}
export function isCaseInsensitiveEqual(a, b) {
    return (a === null || a === void 0 ? void 0 : a.toLowerCase()) === (b === null || b === void 0 ? void 0 : b.toLowerCase());
}
/** The first candidate that is a non-empty string; backend error frames often carry `""` for "absent". */
export function pickString(...candidates) {
    return candidates.find((c) => typeof c === 'string' && c.length > 0);
}
/**
 * Removes all spaces from a string.
 *
 * @param str - The input string
 * @returns A new string with all spaces removed
 * @example removeAllSpaces("hello world") // Returns "helloworld"
 */
export function removeAllSpaces(str) {
    return str.replace(/\s+/g, '');
}
/**
 * Removes all whitespace characters including spaces and newlines from a string.
 *
 * @param str - The input string
 * @returns A new string with all whitespace removed
 * @example removeAllWhitespace("hello world\ntest") // Returns "helloworldtest"
 */
export function removeAllWhitespace(str) {
    return str.replace(/[\s\n]+/g, '');
}
export function caseInsensitiveInclude(a, b) {
    return Boolean(a === null || a === void 0 ? void 0 : a.toLowerCase().includes((b === null || b === void 0 ? void 0 : b.toLowerCase()) || ''));
}
export function capitalizeString(str) {
    var _a;
    return `${(_a = str === null || str === void 0 ? void 0 : str.charAt(0)) === null || _a === void 0 ? void 0 : _a.toUpperCase()}${str === null || str === void 0 ? void 0 : str.slice(1, str.length)}`;
}
export function quoteString(str = '', quote = `"`) {
    // Replace all quotes with escaped quotes
    return quote + str.replaceAll(quote, '\\' + quote) + quote;
}
export function unQuoteString(str = '') {
    // Remove the surrounding quotes if they are double or single quotes
    if ((str.startsWith('"') && str.endsWith('"')) ||
        (str.startsWith("'") && str.endsWith("'")) ||
        (str.startsWith('`') && str.endsWith('`'))) {
        const wrappingQuote = str[0];
        str = str.slice(1, -1);
        // Replace escaped quotes to quotes
        return str.replaceAll(`\\${wrappingQuote}`, wrappingQuote);
    }
    return str;
}
export function getPrefixTruncatedText({ text = '', matchText = '', minPrefixLen = 0, symbol = '...' }) {
    if (!text || !matchText)
        return text;
    const lowerText = text.toLowerCase();
    const matchIndex = lowerText.indexOf(matchText.toLowerCase());
    if (matchIndex === -1 || matchIndex <= minPrefixLen)
        return text;
    const beforeMatch = text.slice(0, matchIndex).trim();
    const truncateAt = Math.max(beforeMatch.lastIndexOf(' ') + 1, 0);
    return truncateAt <= minPrefixLen ? text : symbol + text.slice(truncateAt).trim();
}
/**
 * Computes the article for a given text based on the first letter.
 * @param text - The text to compute the article for.
 * @returns The article for the given text, either 'an' or 'a'.
 */
export const computeArticleForText = (text) => {
    if (!text)
        return 'a';
    const startsWithVowel = ['a', 'e', 'i', 'o', 'u'].includes(text[0].toLowerCase());
    return startsWithVowel ? 'an' : 'a';
};
/**
 * Splits a label into name and email parts using regex to match text between parentheses
 * @param label - The label to split
 * @returns An object with name and email parts
 * @example splitNameAndEmail("John Doe (john@email.com)") // Returns { name: "John Doe", email: "john@email.com" }
 */
export const splitNameAndEmail = (label) => {
    const [name, email] = label.split(/[()]/);
    return { name, email };
};
